import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractToken, thunkErrorHandler } from '~/store/utils.ts';
import api from '~/libs/api';
import {
  TChangePassword,
  TCheckUserRigisterRequest,
  TClientLoginResponse,
  TClientUser,
  TLoginMfaResponse,
  TLoginRequest,
  TManagerLoginResponse,
} from '~/store/users/types.ts';
import * as Sentry from '@sentry/react';
import { TSignUp } from '~/modules/auth/types';
import { TThunkError } from '../covenants/types';
import type { TApiSupervisorInfo } from '~/modules/covenants/types.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import { RootState } from '~/store/store.ts';
import { clearUsersState } from './slices';
import { clearForm } from '../identification-form/slices';
import { IS_AUTHENTICATED_KEY } from '~/modules/auth/constants';
import { TLoginVerify } from '~/store/otp-verification/types.ts';

export const changePassword = createAsyncThunk<null, { form: TChangePassword }>(
  'users/changePassword',
  async ({ form }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/user-service/auth/changePassword`, form);

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const resetPassword = createAsyncThunk<null, { username: string }>(
  'users/resetPassword',
  async ({ username }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/user-service/auth/resetPassword`, {
        params: { username },
      });

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const login = createAsyncThunk<TLoginMfaResponse, TLoginRequest>(
  'users/login',
  async ({ password, username }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        '/user-service/auth/login/mfa',
        { password, username },
        { withCredentials: true }
      );
      Sentry.setUser({ username });
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const loginVerify = createAsyncThunk<TClientLoginResponse, TLoginVerify>(
  'users/loginVerify',
  async ({ mfaToken, verificationToken }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        '/user-service/auth/login/verify',
        { mfaToken, verificationToken },
        { withCredentials: true }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const logout = createAsyncThunk<undefined, undefined>(
  'users/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/user-service/auth/logout');
      Sentry.setUser({ username: '' });

      return;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const checkBeforeRegisterUser = createAsyncThunk<undefined, TCheckUserRigisterRequest>(
  'users/checkBeforeRegisterUser',
  async ({ username, phoneNumber }, { rejectWithValue }) => {
    try {
      await api.post('/user-service/auth/check-before-the-registration', {
        username,
        phoneNumber,
      });
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const registerNewUser = createAsyncThunk<
  TClientUser,
  { data: TSignUp; logoutPreviousSession?: boolean }
>('users/registerNewUser', async ({ data }, { rejectWithValue }) => {
  try {
    const { data: user } = await api.post('/user-service/auth/register', data);
    return user;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const loginManager = createAsyncThunk<
  TManagerLoginResponse,
  { data: TLoginRequest; logoutPreviousSession: boolean }
>('users/loginManager', async ({ data }, { rejectWithValue }) => {
  try {
    const { data: manager } = await api.post('/user-service/auth/active-directory/login', data, {
      withCredentials: true,
    });
    return manager;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const refreshTokenThunk = createAsyncThunk<
  string | null,
  undefined,
  { rejectValue: TThunkError }
>('users/refreshToken', async (_, { rejectWithValue, dispatch }) => {
  try {
    const { data, status, headers } = await api.post('/user-service/auth/refresh', null);
    const token = extractToken(headers['authorization'] as string | undefined, data?.token);

    if ((status === 200 || status === 204) && token) {
      return token;
    }

    return rejectWithValue({
      message: 'Empty token after refresh',
      status: status,
    });
  } catch (error: unknown) {
    dispatch?.(clearUsersState());
    dispatch?.(clearForm());
    localStorage.removeItem(IS_AUTHENTICATED_KEY);
    const errorEntity = error as { data: TThunkError } & TThunkError;
    return rejectWithValue({
      message: errorEntity?.message || errorEntity?.data?.message || 'Refresh failed',
      status: errorEntity?.status,
    });
  }
});

export const fetchSupervisorInfo = createAsyncThunk<
  TApiSupervisorInfo,
  { token: string | null },
  { state: RootState; rejectValue: TThunkError }
>(
  'users/fetchSupervisorInfo',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/covenants-api/covenants/supervisors/supervisor-info');
      return {
        hasApplicationAccess: !!data?.hasApplicationAccess,
        hasAdministrationAccess: !!data?.hasAdministrationAccess,
        hasCallCenterOperatorAccess: !!data?.hasCallCenterOperatorAccess,
        hasBranchManagerAccess: !!data?.hasBranchManagerAccess,
        hasClientViewAccess: !!data?.hasClientViewAccess,
        hasEmailBlackListAccess: !!data?.hasEmailBlackListAccess,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  },
  {
    condition: ({ token }, { getState }) => {
      if (!token) {
        return false;
      }
      const supervisorInfoStatus = getState().users.supervisorInfo.status;
      return (
        supervisorInfoStatus === FetchStatus.Idle || supervisorInfoStatus === FetchStatus.Error
      );
    },
  }
);
